'use client';

import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { motion, useReducedMotion } from 'motion/react';
import {
  ArrowUp,
  CalendarDays,
  Check,
  CornerUpLeft,
  History,
  Mic,
  Paperclip,
  Pause,
  Play,
  ShoppingBag,
  Sparkles,
  User,
  UserPlus,
  type LucideIcon,
} from 'lucide-react';
import { useTranslations, useLocale } from 'next-intl';
import {
  TypographyH2,
  TypographyLead,
  TypographyMuted,
  TypographySmall,
} from '@/components/ui/typography';
import { SectionKicker } from '@/components/brand/section-kicker';
import { Reveal } from '@/components/motion/reveal';
import { SectionLink } from '@/components/brand/section-link';
import { cn } from '@/lib/utils';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

/**
 * "Just ask" — one prompt, cycling through everything Kasbly does. Result cards
 * ride a horizontal carousel; each capability has its OWN result layout (grid,
 * messages, chat, order, …). The prompt below blurs through a gold Gaussian
 * dissolve on each step, over an inner gold glow that only shows while it works.
 */

// Desktop stays capped at 560px; the narrower vw only kicks in on small screens
// so the blurred prev/next slides peek at the edges instead of sitting off-screen.
const SLIDE = 'min(560px, 68vw)';
const GAP = 20;
const COPIES = 3;

// Four cards — ONLY the capabilities the Stack gates don't already tell:
// follow-ups, lead capture, campaign steering, business answers. Order is
// aligned by index with `justAsk.capabilities` in the messages.
const CAP_ICONS: LucideIcon[] = [History, UserPlus, CalendarDays, ShoppingBag];

interface Cap {
  label: string;
  prompt: string;
  result: string;
  variant: string;
  badge?: string;
  grid?: { a: string; b: string }[];
  tags?: string[];
  msgs?: { who: string; text: string }[];
  footer?: string;
  q?: string;
  a?: string;
  status?: string;
  cta?: string;
  rows?: { k: string; v: string }[];
  slots?: { time: string; channel: string }[];
  tag?: string;
  product?: string;
  price?: string;
  paid?: string;
  pills?: string[];
  sample?: string;
}

function CardBody({ cap }: { cap: Cap }) {
  switch (cap.variant) {
    case 'grid':
      return (
        <>
          <div className="grid grid-cols-2 gap-2.5">
            {cap.grid!.map((g) => (
              <div
                key={g.a}
                className="flex items-center gap-2 rounded-lg border border-border bg-secondary/25 px-3 py-2.5"
              >
                <Check className="size-4 shrink-0 text-primary" aria-hidden />
                <div className="min-w-0">
                  <span className="block truncate text-caption font-semibold text-foreground">
                    {g.a}
                  </span>
                  <span className="block truncate text-caption text-muted-foreground">{g.b}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {cap.tags!.map((tg) => (
              <span
                key={tg}
                className="rounded-full bg-primary px-3 py-1 text-caption font-medium text-primary-foreground"
              >
                {tg}
              </span>
            ))}
          </div>
        </>
      );
    case 'messages':
      return (
        <>
          <ul className="space-y-3">
            {cap.msgs!.map((m) => (
              <li key={m.who} className="flex items-center gap-3">
                <span className="grid size-8 shrink-0 place-items-center rounded-full bg-primary/12 text-primary">
                  <User className="size-4" aria-hidden />
                </span>
                <span className="block min-w-0 flex-1 truncate text-small text-muted-foreground">
                  <span className="font-semibold text-foreground">{m.who}</span> — {m.text}
                </span>
                <CornerUpLeft
                  className="size-4 shrink-0 text-primary rtl:-scale-x-100"
                  aria-hidden
                  data-directional-icon-decorative
                />
              </li>
            ))}
          </ul>
          <span className="mt-4 block text-caption font-medium text-primary">{cap.footer}</span>
        </>
      );
    case 'chat':
      return (
        <>
          <div className="space-y-2">
            <div className="flex">
              <span className="max-w-[82%] rounded-2xl rounded-es-sm bg-secondary/50 px-3.5 py-2 text-small text-foreground">
                {cap.q}
              </span>
            </div>
            <div className="flex justify-end">
              <span className="max-w-[82%] rounded-2xl rounded-ee-sm bg-primary px-3.5 py-2 text-small text-primary-foreground">
                {cap.a}
              </span>
            </div>
          </div>
          <div className="mt-4 flex items-center justify-between gap-3">
            <span className="text-caption text-muted-foreground">{cap.status}</span>
            <span className="shrink-0 rounded-full border border-primary px-3 py-1 text-caption font-semibold text-primary">
              {cap.cta}
            </span>
          </div>
        </>
      );
    case 'contact':
      return (
        <>
          <dl>
            {cap.rows!.map((r) => (
              <div
                key={r.k}
                className="flex items-center justify-between border-b border-dashed border-border py-2.5 last:border-0"
              >
                <dt className="text-small text-muted-foreground">{r.k}</dt>
                <dd className="text-small font-medium text-foreground">{r.v}</dd>
              </div>
            ))}
          </dl>
          <span className="mt-4 inline-block rounded-full bg-primary px-4 py-1.5 text-caption font-semibold text-primary-foreground">
            {cap.cta}
          </span>
        </>
      );
    case 'schedule':
      return (
        <>
          <ul className="space-y-3">
            {cap.slots!.map((s) => (
              <li key={s.time} className="flex items-center gap-3">
                <span className="rounded-md bg-primary/12 px-2 py-1 text-caption font-semibold text-primary">
                  {s.time}
                </span>
                <span className="flex-1 text-small text-foreground">{s.channel}</span>
                <Check className="size-4 shrink-0 text-primary" aria-hidden />
              </li>
            ))}
          </ul>
          <span className="mt-4 block text-caption font-medium text-primary">{cap.tag}</span>
        </>
      );
    case 'order':
      return (
        <>
          <div className="flex items-center gap-3 rounded-lg border border-border bg-secondary/25 p-3">
            <span className="grid size-10 shrink-0 place-items-center rounded-md bg-secondary/60 text-primary">
              <ShoppingBag className="size-5" aria-hidden />
            </span>
            <div className="min-w-0 flex-1">
              <span className="block truncate text-small font-semibold text-foreground">
                {cap.product}
              </span>
              <span className="block text-caption text-muted-foreground">{cap.price}</span>
            </div>
            <span className="shrink-0 rounded-full bg-primary px-3 py-1 text-caption font-semibold text-primary-foreground">
              {cap.paid}
            </span>
          </div>
          <span className="mt-4 inline-block rounded-full border border-primary px-4 py-1.5 text-caption font-semibold text-primary">
            {cap.cta}
          </span>
        </>
      );
    case 'pills':
      return (
        <>
          <div className="flex flex-wrap gap-2">
            {cap.pills!.map((pl) => (
              <span
                key={pl}
                className="rounded-full bg-secondary/60 px-3 py-1 text-caption font-medium text-foreground"
              >
                {pl}
              </span>
            ))}
          </div>
          <span className="mt-3 block rounded-lg bg-secondary/25 px-3.5 py-2.5 text-small italic text-muted-foreground">
            {cap.sample}
          </span>
          <span className="mt-3 inline-block rounded-full border border-primary px-4 py-1.5 text-caption font-semibold text-primary">
            {cap.cta}
          </span>
        </>
      );
    default:
      return null;
  }
}

/** One carousel slide — a framed result card with its own layout. */
function ResultCard({ index, cap }: { index: number; cap: Cap }) {
  const TitleIcon = CAP_ICONS[index] ?? CalendarDays;
  return (
    <div className="h-full rounded-2xl border border-primary/15 bg-primary/[0.07] p-2.5">
      <Card className="flex h-full min-h-[20rem] flex-col p-5 sm:p-6">
        <div className="flex items-center gap-3">
          <span className="grid size-11 shrink-0 place-items-center rounded-lg bg-primary text-primary-foreground">
            <TitleIcon className="size-5" aria-hidden />
          </span>
          <span className="flex flex-1 items-center gap-2 font-display text-h4 text-foreground">
            <Check className="size-5 shrink-0 text-primary" aria-hidden />
            {cap.result}
          </span>
          {cap.badge ? (
            <span className="shrink-0 rounded-full bg-primary/12 px-2.5 py-1 text-caption font-semibold text-primary">
              {cap.badge}
            </span>
          ) : null}
        </div>
        <div className="my-4 border-t border-dashed border-border" />
        <CardBody cap={cap} />
      </Card>
    </div>
  );
}

export function JustAsk() {
  const t = useTranslations('justAsk');
  const isRtl = useLocale() === 'ar';
  const caps = t.raw('capabilities') as Cap[];
  const n = caps.length;
  const reduce = useReducedMotion();

  const [pos, setPos] = useState(n);
  const [snap, setSnap] = useState(false);
  const [nudge, setNudge] = useState(0);
  const [paused, setPaused] = useState(false);
  const [hoverPaused, setHoverPaused] = useState(false);
  const [focusPaused, setFocusPaused] = useState(false);
  const active = ((pos % n) + n) % n;
  const autoAdvancePaused = Boolean(reduce) || paused || hoverPaused || focusPaused;

  // prompt text: a single element that blurs out → swaps → blurs back in.
  // Depend only on `active` (not shownIdx) so the mid-sequence swap doesn't
  // cancel the un-blur timeout.
  const [shownIdx, setShownIdx] = useState(active);
  const [textBlur, setTextBlur] = useState(false);
  const firstText = useRef(true);

  useEffect(() => {
    if (reduce || firstText.current) {
      firstText.current = false;
      setShownIdx(active);
      return;
    }
    setTextBlur(true);
    const t1 = setTimeout(() => setShownIdx(active), 300);
    const t2 = setTimeout(() => setTextBlur(false), 340);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [active, reduce]);

  useEffect(() => {
    if (autoAdvancePaused) return;
    const id = setInterval(() => setPos((p) => p + 1), 3600);
    return () => clearInterval(id);
  }, [autoAdvancePaused, nudge]);

  useEffect(() => {
    if (pos >= 2 * n) {
      const id = setTimeout(() => {
        setSnap(true);
        setPos((p) => p - n);
      }, 720);
      return () => clearTimeout(id);
    }
    if (pos < n) {
      const id = setTimeout(() => {
        setSnap(true);
        setPos((p) => p + n);
      }, 720);
      return () => clearTimeout(id);
    }
  }, [pos, n]);

  useEffect(() => {
    if (!snap) return;
    const id = requestAnimationFrame(() => requestAnimationFrame(() => setSnap(false)));
    return () => cancelAnimationFrame(id);
  }, [snap]);

  const select = (i: number) => {
    setSnap(false);
    setPos(n + i);
    setNudge((x) => x + 1);
  };

  const slides = Array.from({ length: COPIES * n }, (_, k) => k);
  const trackTransform = `translateX(calc(50vw - ${SLIDE} / 2 - ${pos} * (${SLIDE} + ${GAP}px)))`;

  return (
    <section
      id="ask"
      className="section overflow-hidden"
      onMouseEnter={() => setHoverPaused(true)}
      onMouseLeave={() => setHoverPaused(false)}
      onFocusCapture={() => setFocusPaused(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setFocusPaused(false);
        }
      }}
    >
      <div className="container-brand">
        <Reveal className="mx-auto max-w-2xl text-center">
          <SectionKicker>{t('kicker')}</SectionKicker>
          <TypographyH2 className="mx-auto mt-5 max-w-[30rem] border-b-0 pb-0 font-display text-primary">
            {t('heading')}
          </TypographyH2>
          <TypographyLead className="mx-auto mt-4 max-w-xl text-muted-foreground">
            {t('lede')}
          </TypographyLead>
        </Reveal>

        <Image
          src="/ask-pencil.webp"
          alt=""
          aria-hidden
          width={1905}
          height={825}
          draggable={false}
          className="pointer-events-none mx-auto mt-4 w-full max-w-2xl select-none"
        />
      </div>

      {/* Full-bleed sliding carousel of result cards */}
      <div
        className="relative mt-4 overflow-hidden"
        style={{
          marginInline: 'calc(50% - 50vw)',
          width: '100vw',
          WebkitMaskImage:
            'linear-gradient(to right, transparent, #000 24px, #000 calc(100% - 24px), transparent)',
          maskImage:
            'linear-gradient(to right, transparent, #000 24px, #000 calc(100% - 24px), transparent)',
        }}
      >
        {/* The slider mechanics (flex order + translateX) are LTR-only maths;
            forcing dir="ltr" here keeps the track on-screen in Arabic. Each
            slide re-asserts the real direction so its card content stays RTL. */}
        <div
          dir="ltr"
          className="flex items-stretch"
          style={{
            gap: `${GAP}px`,
            transform: trackTransform,
            transition: snap ? 'none' : 'transform 0.7s cubic-bezier(0.4,0,0.2,1)',
            willChange: 'transform',
          }}
        >
          {slides.map((k) => {
            const isActive = k === pos;
            return (
              <div
                key={k}
                dir={isRtl ? 'rtl' : 'ltr'}
                aria-hidden={!isActive}
                className="shrink-0"
                style={{
                  width: SLIDE,
                  opacity: isActive ? 1 : 0.4,
                  filter: isActive ? 'none' : 'blur(3px)',
                  transition: 'opacity 0.7s, filter 0.7s',
                }}
              >
                <ResultCard index={k % n} cap={caps[k % n]!} />
              </div>
            );
          })}
        </div>
      </div>

      <div className="container-brand">
        <div className="mt-5 flex justify-center">
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-pressed={paused}
            onClick={() => setPaused((current) => !current)}
          >
            {paused ? <Play aria-hidden /> : <Pause aria-hidden />}
            <TypographySmall>{paused ? t('resume') : t('pause')}</TypographySmall>
          </Button>
        </div>

        {/* Prompt bar — inner gold glow shows only while it works; the prompt
            dissolves through a smooth gold Gaussian blur on each step */}
        <div className="mx-auto mt-8 max-w-[720px]">
          <div className="relative flex items-center gap-3 overflow-hidden rounded-full bg-primary px-6 py-4 sm:py-[1.2rem]">
            {!reduce ? (
              <motion.span
                key={`beam-${active}`}
                aria-hidden
                className="ja-beam-conic pointer-events-none absolute inset-0"
                initial={{ opacity: 0 }}
                animate={{ opacity: [0, 0.9, 0] }}
                transition={{ duration: 1.9, ease: 'easeInOut', times: [0, 0.4, 1] }}
              />
            ) : null}

            <Paperclip
              className="relative z-10 size-5 shrink-0 text-primary-foreground/60"
              aria-hidden
            />
            <div className="relative z-10 min-h-6 flex-1">
              <motion.span
                className="block whitespace-nowrap text-body"
                animate={{
                  filter: textBlur && !reduce ? 'blur(8px)' : 'blur(0px)',
                  opacity: textBlur && !reduce ? 0.75 : 1,
                  color: textBlur && !reduce ? '#e9b949' : '#fbf7ee',
                }}
                transition={{ duration: 0.34, ease: 'easeInOut' }}
              >
                {caps[shownIdx]!.prompt}
              </motion.span>
            </div>
            <Mic className="relative z-10 size-5 shrink-0 text-primary-foreground/55" aria-hidden />
            <span className="relative z-10 grid size-11 shrink-0 place-items-center rounded-full bg-brand-gold text-[#2a2207]">
              <ArrowUp className="size-5" aria-hidden />
            </span>
          </div>
        </div>

        {/* Capability chips — the active one is highlighted as it auto-cycles */}
        <div className="mx-auto mt-5 grid max-w-[720px] grid-cols-1 gap-3 sm:grid-cols-2">
          {caps.map((c, i) => {
            const Icon = CAP_ICONS[i] ?? Sparkles;
            const on = i === active;
            return (
              <div
                key={c.label}
                role="button"
                tabIndex={0}
                onClick={() => select(i)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    select(i);
                  }
                }}
                aria-pressed={on}
                className={cn(
                  'flex cursor-pointer items-center gap-3 rounded-full border px-4 py-3 text-start transition-brand',
                  on
                    ? 'border-primary bg-primary/10'
                    : 'border-border bg-card hover:border-primary/40',
                )}
              >
                <Icon className="size-5 shrink-0 text-primary" aria-hidden />
                <span className="flex-1 text-small font-medium text-foreground">{c.label}</span>
                <span className="grid size-7 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground">
                  <ArrowUp className="size-4" aria-hidden />
                </span>
              </div>
            );
          })}
        </div>

        <div className="mt-8 flex justify-center">
          <SectionLink href="/products/sell#crm">{t('learnMore')}</SectionLink>
        </div>

        <Image
          src="/ask-door.webp"
          alt=""
          aria-hidden
          width={2172}
          height={724}
          draggable={false}
          className="pointer-events-none mx-auto mt-10 w-full max-w-md select-none"
        />

        <TypographyMuted className="mx-auto mt-2 max-w-md text-center">
          {t('footer')}
        </TypographyMuted>
      </div>
    </section>
  );
}
